import { useEffect } from "react";
import { SITE_URL, BRAND } from "@/lib/site";

/**
 * Technical SEO component using React 19 native document metadata hoisting for title/meta/link,
 * and injecting JSON-LD structured data into <head> safely.
 */
export const SEO = ({
  title,
  description = BRAND.description,
  path = "/",
  image,
  type = "website",
  schema,
}) => {
  const formattedTitle = title
    ? title.includes(BRAND.name) || title.includes(BRAND.shortName)
      ? title
      : `${title} | ${BRAND.name}`
    : `${BRAND.name} | ${BRAND.tagline}`;

  // Ensure clean path with leading slash and no trailing slash unless root
  const cleanPath = path === "/" ? "/" : path.startsWith("/") ? path.replace(/\/+$/, "") : `/${path.replace(/\/+$/, "")}`;
  const canonical = `${SITE_URL}${cleanPath}`;
  const ogImage = image || "https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=1200&q=80";

  useEffect(() => {
    // Clear any pre-existing JSON-LD scripts to prevent runtime duplication
    const existing = document.querySelectorAll('script[data-seo-jsonld="true"]');
    existing.forEach((script) => script.remove());

    if (!schema) return;
    const list = Array.isArray(schema) ? schema.filter(Boolean) : [schema];
    const nodes = list.map((data) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-seo-jsonld", "true");
      s.text = JSON.stringify(data);
      document.head.appendChild(s);
      return s;
    });
    return () => nodes.forEach((n) => n.remove());
  }, [schema]);

  return (
    <>
      <title>{formattedTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      
      {/* Open Graph Tags */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={BRAND.name} />
      <meta property="og:title" content={formattedTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={formattedTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
    </>
  );
};

